import { Hash, MerkleProof } from '@maco144/shared';

// Browser-compatible hash using Web Crypto API
export async function hashAsync(data: string): Promise<Hash> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

// Synchronous hash using a simple implementation (for compatibility)
// In production, use hashAsync where possible
export function hash(data: string): Hash {
  // Simple SHA-256 implementation for browser
  // This is a fallback - prefer hashAsync for actual use
  return sha256(data);
}

export function hashConcat(...values: (string | Hash)[]): Hash {
  return hash(values.join(':'));
}

// Length-prefixed variant matching @maco144/shared's hashConcat encoding
// (`${value.length}:${value}` per segment, vs. this file's own plain
// colon-join above). Use this specifically when a browser-computed hash
// needs to match what @maco144/shared's hash() would produce under Node
// for the same inputs — e.g. deriving the same UserId/RegionId a server or
// agent-sdk (Node) client would derive from the same source values.
export function hashConcatLengthPrefixed(...values: string[]): Hash {
  return hash(values.map((v) => `${v.length}:${v}`).join(''));
}

// Minimal SHA-256 implementation for synchronous browser use
function sha256(message: string): string {
  // Constants
  const K: number[] = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];

  // Initial hash values
  let H = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ];

  // Pre-processing
  const bytes: number[] = [];
  for (let i = 0; i < message.length; i++) {
    bytes.push(message.charCodeAt(i));
  }

  const bitLength = bytes.length * 8;
  bytes.push(0x80);

  while (bytes.length % 64 !== 56) {
    bytes.push(0);
  }

  // Append length as 64-bit big-endian. JS bitwise ops only support 0-31 bit
  // shifts (shift amounts wrap mod 32), so `bitLength >>> (i * 8)` for i >= 4
  // doesn't yield zero as intended — it silently duplicates the low 32 bits
  // into the high 4 bytes, corrupting the padding for any non-empty message.
  // Split into high/low 32-bit halves via division (safe up to 2^53) instead.
  const bitLengthHigh = Math.floor(bitLength / 0x100000000);
  const bitLengthLow = bitLength >>> 0;
  for (let i = 3; i >= 0; i--) {
    bytes.push((bitLengthHigh >>> (i * 8)) & 0xff);
  }
  for (let i = 3; i >= 0; i--) {
    bytes.push((bitLengthLow >>> (i * 8)) & 0xff);
  }

  // Process each 512-bit block
  for (let block = 0; block < bytes.length; block += 64) {
    const W: number[] = new Array(64);

    // Prepare message schedule
    for (let t = 0; t < 16; t++) {
      W[t] =
        (bytes[block + t * 4] << 24) |
        (bytes[block + t * 4 + 1] << 16) |
        (bytes[block + t * 4 + 2] << 8) |
        bytes[block + t * 4 + 3];
    }

    for (let t = 16; t < 64; t++) {
      const s0 = rightRotate(W[t - 15], 7) ^ rightRotate(W[t - 15], 18) ^ (W[t - 15] >>> 3);
      const s1 = rightRotate(W[t - 2], 17) ^ rightRotate(W[t - 2], 19) ^ (W[t - 2] >>> 10);
      W[t] = (W[t - 16] + s0 + W[t - 7] + s1) >>> 0;
    }

    // Working variables
    let [a, b, c, d, e, f, g, h] = H;

    // Compression
    for (let t = 0; t < 64; t++) {
      const S1 = rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + S1 + ch + K[t] + W[t]) >>> 0;
      const S0 = rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (S0 + maj) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    // Update hash
    H = [
      (H[0] + a) >>> 0,
      (H[1] + b) >>> 0,
      (H[2] + c) >>> 0,
      (H[3] + d) >>> 0,
      (H[4] + e) >>> 0,
      (H[5] + f) >>> 0,
      (H[6] + g) >>> 0,
      (H[7] + h) >>> 0,
    ];
  }

  // Convert to hex
  return H.map((h) => h.toString(16).padStart(8, '0')).join('');
}

function rightRotate(value: number, amount: number): number {
  return ((value >>> amount) | (value << (32 - amount))) >>> 0;
}

// Sparse Merkle Tree for browser
export class BrowserSparseMerkleTree {
  private nodes: Map<string, Hash> = new Map();
  private depth: number;
  private emptyHashes: Hash[];

  constructor(depth: number = 16) {
    this.depth = depth;
    this.emptyHashes = this.computeEmptyHashes();
  }

  private computeEmptyHashes(): Hash[] {
    const hashes: Hash[] = new Array(this.depth + 1);
    hashes[0] = hash('empty');
    for (let i = 1; i <= this.depth; i++) {
      hashes[i] = hashConcat(hashes[i - 1], hashes[i - 1]);
    }
    return hashes;
  }

  private keyAt(index: bigint, level: number): string {
    return `${level}:${index.toString()}`;
  }

  set(index: bigint, value: Hash): void {
    let currentHash = value;
    let currentIndex = index;

    this.nodes.set(this.keyAt(currentIndex, 0), currentHash);

    for (let level = 0; level < this.depth; level++) {
      const isRight = currentIndex % 2n === 1n;
      const siblingIndex = isRight ? currentIndex - 1n : currentIndex + 1n;
      const siblingKey = this.keyAt(siblingIndex, level);
      const siblingHash = this.nodes.get(siblingKey) ?? this.emptyHashes[level];

      const parentIndex = currentIndex / 2n;
      const parentHash = isRight
        ? hashConcat(siblingHash, currentHash)
        : hashConcat(currentHash, siblingHash);

      this.nodes.set(this.keyAt(parentIndex, level + 1), parentHash);
      currentHash = parentHash;
      currentIndex = parentIndex;
    }
  }

  getRoot(): Hash {
    return this.nodes.get(this.keyAt(0n, this.depth)) ?? this.emptyHashes[this.depth];
  }

  getProof(index: bigint): MerkleProof {
    const path: MerkleProof['path'] = [];
    let currentIndex = index;

    for (let level = 0; level < this.depth; level++) {
      const isRight = currentIndex % 2n === 1n;
      const siblingIndex = isRight ? currentIndex - 1n : currentIndex + 1n;
      const siblingKey = this.keyAt(siblingIndex, level);
      const siblingHash = this.nodes.get(siblingKey) ?? this.emptyHashes[level];

      path.push({
        hash: siblingHash,
        direction: isRight ? 'left' : 'right',
      });

      currentIndex = currentIndex / 2n;
    }

    const leafKey = this.keyAt(index, 0);
    const leaf = this.nodes.get(leafKey) ?? this.emptyHashes[0];

    return {
      leaf,
      path,
      root: this.getRoot(),
    };
  }

  static verify(proof: MerkleProof): boolean {
    let currentHash = proof.leaf;

    for (const step of proof.path) {
      currentHash =
        step.direction === 'left'
          ? hashConcat(step.hash, currentHash)
          : hashConcat(currentHash, step.hash);
    }

    return currentHash === proof.root;
  }
}
